import React from 'react';
import {useTranslation, I18nextProvider} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {LiveThemeProvider} from '@tickchak/react-share/live';
import '@tickchak/react-share/live/index.css';
import * as css from './ETicket.css';
import TicketCardCarousel from './TicketCardCarousel/TicketCardCarousel';
import TicketCard from './TicketCard/TicketCard';
import TicketMenue from './TicketMenue/TicketMenue';
import {useETicketPages} from './hooks/useETicketPages';
import {useETicketDir} from './hooks/useETicketDir';
import {RootState} from '../../redux/store.type';
import Page404 from '../page404/Page404';
import {ETICKET_TOKEN_LENGTH} from './ETicket.loader';

// Cache the language-scoped i18n clone OUTSIDE the component. A useMemo is discarded
// whenever a render suspends, so cloning inside render created a fresh (not-yet-ready)
// instance on every suspense retry — each retry suspended on its own new clone, looping
// until a race broke through (instant in a focused tab; ~100s in a background tab where
// the browser throttles suspense-retry timers).
const scopedInstances = {};
const getScopedI18n = (i18n, lng) => {
    if(!scopedInstances[lng]) {
        scopedInstances[lng] = i18n.cloneInstance({lng});
    }
    return scopedInstances[lng];
};

function ETicket() {
    const {i18n} = useTranslation('eTicket');
    const {token} = useParams();
    const {pages} = useETicketPages();
    const data = useSelector((state: RootState) => state.eTicket.data);
    const {dir, i18nLang} = useETicketDir();
    const scopedI18n = getScopedI18n(i18n, i18nLang);

    if(!token || token.length !== ETICKET_TOKEN_LENGTH) {
        return <Page404 eidParam="" pidParam="" />;
    }

    return (
        <LiveThemeProvider>
            <css.liveFont />
            <I18nextProvider i18n={scopedI18n}>
                <css.stage $colors={data?.event?.imageColors} dir={dir} data-cy="entrance-ticket-page">
                    {['l1', 'l2', 'l3'].map(layer => (
                        <css.starLayer key={layer} className={layer} aria-hidden />
                    ))}
                    <TicketCardCarousel overlay={<TicketMenue />}>
                        {pages.map(({key, withLink, hasFooter, TopComp, topProps, Comp, props}) => (
                            <TicketCard
                                key={key}
                                top={TopComp ? <TopComp {...(topProps ?? {})} /> : null}
                                withLink={withLink}
                                hasFooter={hasFooter}>
                                {Comp ? <Comp {...props} /> : null}
                            </TicketCard>
                        ))}
                    </TicketCardCarousel>
                </css.stage>
            </I18nextProvider>
        </LiveThemeProvider>
    );
}

export default ETicket;
